import { supabase } from '@/integrations/supabase/client';

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & { callMethod?: unknown; queue?: unknown[] };
    _fbq?: unknown;
  }
}

/** Meta Pixel ID (public by design — it lives in the page source). */
export const META_PIXEL_ID = '1604324734388087';

export const CONSENT_STORAGE_KEY = 'ngs_cookie_consent';
export const CONSENT_EVENT = 'ngs:cookie-consent';
/** Fired to (re)open the cookie preferences banner from anywhere in the app. */
export const CONSENT_OPEN_EVENT = 'ngs:cookie-consent-open';

export const PRIVACY_VERSION = '2.0';
export const TERMS_VERSION = '2.0';

export interface ConsentState {
  essential: boolean;
  marketing: boolean;
  privacyPolicyVersion?: string;
  termsVersion?: string;
  ageVerified?: boolean;
  updatedAt: string;
}

export type ConsentValue = 'granted' | 'denied';

/** Ask the consent banner to open so the visitor can change their choice. */
export function openCookiePreferences() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
}

function parseStored(raw: string | null): ConsentState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed.essential === 'boolean' && typeof parsed.marketing === 'boolean') {
      return parsed as ConsentState;
    }
  } catch {
    // legacy string value
  }
  return null;
}

/** Current consent state, or null when the user has not decided yet. */
export function getConsent(): ConsentState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    const state = parseStored(raw);
    if (state) return state;
    // Backward compatibility: convert old binary consent.
    if (raw === 'granted' || raw === 'denied') {
      return {
        essential: true,
        marketing: raw === 'granted',
        updatedAt: new Date().toISOString(),
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function hasMarketingConsent() {
  return getConsent()?.marketing === true;
}

export function hasEssentialConsent() {
  const c = getConsent();
  return c ? c.essential === true : null;
}

function persistConsent(state: ConsentState) {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable — consent stays session-only */
  }
}

async function logConsent(state: ConsentState) {
  try {
    const user = (await supabase.auth.getUser()).data.user;
    await supabase.from('consent_log').insert([
      {
        user_id: user?.id || null,
        consent_type: 'cookies_marketing',
        granted: state.marketing,
        version: PRIVACY_VERSION,
        metadata: {
          essential: state.essential,
          privacyPolicyVersion: state.privacyPolicyVersion,
          termsVersion: state.termsVersion,
          ageVerified: state.ageVerified,
        },
      },
      {
        user_id: user?.id || null,
        consent_type: 'cookies_essential',
        granted: state.essential,
        version: PRIVACY_VERSION,
        metadata: { marketing: state.marketing },
      },
    ]);
  } catch {
    // Non-blocking: consent still applies even if logging fails.
  }
}

/** Persist the user's decision and start/stop tracking accordingly. */
export function setConsent(value: ConsentValue) {
  const state: ConsentState = {
    essential: true,
    marketing: value === 'granted',
    updatedAt: new Date().toISOString(),
  };
  persistConsent(state);
  logConsent(state);
  if (state.marketing) {
    if (typeof window.fbq === 'function') window.fbq('consent', 'grant');
    initMetaPixel();
  } else if (typeof window.fbq === 'function') {
    // Pixel already loaded this session: stop all further tracking.
    window.fbq('consent', 'revoke');
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: state }));
}

/** Persist granular consent choices. */
export function setGranularConsent(state: Omit<ConsentState, 'updatedAt'>) {
  const full: ConsentState = { ...state, updatedAt: new Date().toISOString() };
  persistConsent(full);
  logConsent(full);
  if (full.marketing) {
    if (typeof window.fbq === 'function') window.fbq('consent', 'grant');
    initMetaPixel();
  } else if (typeof window.fbq === 'function') {
    window.fbq('consent', 'revoke');
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: full }));
}

let initialized = false;

/**
 * Load the Meta Pixel base code and fire the initial PageView.
 * Safe to call multiple times — the script is injected only once, so the
 * landing PageView is never duplicated.
 */
export function initMetaPixel() {
  if (typeof window === 'undefined' || initialized || !hasMarketingConsent()) return;
  initialized = true;

  /* eslint-disable */
  (function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode!.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq!('init', META_PIXEL_ID);
  window.fbq!('track', 'PageView');
}

/** Track a standard Meta event (PageView, ViewContent, AddToCart, Purchase...). */
export function fbTrack(event: string, params?: Record<string, unknown>, options?: { eventID?: string }) {
  if (typeof window === 'undefined' || !hasMarketingConsent()) return;
  if (typeof window.fbq !== 'function') return;
  if (options?.eventID) window.fbq('track', event, params, options);
  else window.fbq('track', event, params);
}

/** Track a custom (non-standard) Meta event. */
export function fbTrackCustom(event: string, params?: Record<string, unknown>) {
  if (typeof window === 'undefined' || !hasMarketingConsent()) return;
  if (typeof window.fbq !== 'function') return;
  window.fbq('trackCustom', event, params);
}

export interface PixelContent {
  id: string;
  quantity: number;
  item_price: number;
  name?: string;
}

const CURRENCY = 'USD';

function contentPayload(contents: PixelContent[], value: number, extra?: Record<string, unknown>) {
  return {
    currency: CURRENCY,
    value: Number(value.toFixed(2)),
    content_type: 'product',
    content_ids: contents.map((c) => c.id),
    contents: contents.map((c) => ({ id: c.id, quantity: c.quantity, item_price: c.item_price })),
    ...extra,
  };
}

export function trackViewContent(content: PixelContent) {
  fbTrack('ViewContent', contentPayload([content], content.item_price * content.quantity, {
    content_name: content.name,
  }));
}

export function trackAddToCart(content: PixelContent) {
  fbTrack('AddToCart', contentPayload([content], content.item_price * content.quantity, {
    content_name: content.name,
  }));
}

export function trackInitiateCheckout(contents: PixelContent[], value: number) {
  fbTrack('InitiateCheckout', contentPayload(contents, value, {
    num_items: contents.reduce((sum, c) => sum + c.quantity, 0),
  }));
}

/**
 * Fire Purchase once per order. The same order can be confirmed both in
 * checkout and on the payment-return page, so the order id doubles as the
 * dedupe key locally and as Meta's `eventID` for server-side dedupe.
 */
export function trackPurchaseOnce(orderId: string, contents: PixelContent[], value: number) {
  if (typeof window === 'undefined' || !hasMarketingConsent()) return;
  const key = `ngs_fb_purchase_${orderId}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
  } catch {
    /* ignore */
  }
  fbTrack('Purchase', contentPayload(contents, value, {
    num_items: contents.reduce((sum, c) => sum + c.quantity, 0),
    order_id: orderId,
  }), { eventID: `purchase-${orderId}` });
}
